import { getModulesFromGraph, sanitizeModuleName } from './modules';

// Pre-computed style maps for O(1) lookup instead of switch statements
const LINK_STYLES = {
  Belt: {
    stroke: '#77dd88',
    strokeWidth: 1,
    strokeDasharray: '0',
    sourceMarker: { type: 'path', d: 'M 0 0 0 0' },
    targetMarker: { type: 'path', d: 'M 10 -5 0 0 10 5 z' },
  },
  Bins: {
    stroke: '#5555ff',
    strokeWidth: 1,
    strokeDasharray: '0',
    sourceMarker: { type: 'path', d: 'M 0 0 0 0' },
    targetMarker: { type: 'path', d: 'M 10 -5 0 0 10 5 z' },
  },
  EmptyBins: {
    stroke: '#99dddd',
    strokeWidth: 1,
    strokeDasharray: '5.5',
    sourceMarker: { type: 'path', d: 'M 0 0 0 0' },
    targetMarker: {
      type: 'path',
      d: 'M 10 -5 0 0 10 5 z',
      transform: 'rotate(180)',
    },
  },
  default: { stroke: '#000000', strokeWidth: 1, strokeDasharray: '0' },
};

const STROKE_MAP = {
  BE: 'red',
  DC: 'red',
  PIM: 'orange',
  CP: 'orange',
  CC: 'orange',
  CU: 'orange',
  MEB: '#353535',
  DEB: '#353535',
  ECH: '#353535',
  PFT: 'green',
  DM: 'green',
  REBUT: 'green',
  STOCK: '#ffffff',
  CONV: '#353535',
  DIV: '#353535',
};

// Cache for node styles to avoid re-creating objects
const nodeStyleCache = new Map();

const getNodeStyleByType = (cellType) => {
  if (nodeStyleCache.has(cellType)) {
    return nodeStyleCache.get(cellType);
  }

  const code = cellType?.toUpperCase().split('_')[0];
  let style;

  if (code === 'TRI') {
    style = {
      body: {
        fill: 'transparent',
        stroke: '#bebebe',
        strokeWidth: 4,
        opacity: 1,
      },
      label: {
        fill: '#ffffff',
        fontWeight: 'Bold',
        fontSize: 6,
        opacity: 1,
      },
      chevron1: { opacity: 1 },
      chevron2: { opacity: 1 },
    };
  } else {
    style = {
      body: {
        stroke: STROKE_MAP[code] || '#353535',
        strokeWidth: 1,
        opacity: 1,
        fill: '#ffffff',
      },
      label: {
        fontSize: 6,
        fill: '#353535',
        text: cellType,
        fontWeight: 'Bold',
      },
    };

    if (code === 'STOCK') {
      style.top = { fill: '#ffffff', stroke: '#353535' };
      style.bottom = { fill: '#ffffff', stroke: '#ffffff' };
    }
  }

  nodeStyleCache.set(cellType, style);
  return style;
};

export const importGraphJsonFile = (domainData) => {
  const { graph } = domainData;
  const { transport = [], ...nodesByCategory } = graph;
  let tables = domainData.tables || {};
  if (Array.isArray(tables)) {
    tables = {};
  }

  // Migrate stockChoiceTable from old format to new format
  // Old format: entryModule, exitModule, advanceLevel (string), stockName
  // Intermediate format: entryModule, exitModule, anticipationThreshold (number), stockName
  // New format: currentModule, exitModule, earlyThreshold (integer seconds), stockName
  if (Array.isArray(tables.stockChoiceTable)) {
    tables.stockChoiceTable = tables.stockChoiceTable.map((row) => {
      // If already has earlyThreshold, assume new format - just ensure field names are correct
      if (row.earlyThreshold !== undefined) {
        return {
          currentModule: row.currentModule || row.entryModule || '',
          exitModule: row.exitModule || '',
          earlyThreshold: row.earlyThreshold,
          stockName: row.stockName || '',
        };
      }
      // Handle intermediate format with anticipationThreshold
      if (row.anticipationThreshold !== undefined) {
        return {
          currentModule: row.currentModule || row.entryModule || '',
          exitModule: row.exitModule || '',
          earlyThreshold: row.anticipationThreshold,
          stockName: row.stockName || '',
        };
      }
      // Convert old format with advanceLevel to new format with earlyThreshold
      // Migration values from Python script: Early -> 0, VeryEarly -> 5400
      let earlyThreshold = 0;
      if (row.advanceLevel === 'VeryEarly') {
        earlyThreshold = 5400;
      } else if (row.advanceLevel === 'Early') {
        earlyThreshold = 0;
      } else if (typeof row.advanceLevel === 'number') {
        earlyThreshold = row.advanceLevel;
      }
      return {
        currentModule: row.entryModule || '',
        exitModule: row.exitModule || '',
        earlyThreshold,
        stockName: row.stockName || '',
      };
    });
  }

  // Pre-allocate array with estimated size for better performance
  const nodeCategories = Object.values(nodesByCategory);
  let totalNodes = 0;
  for (let i = 0; i < nodeCategories.length; i++) {
    totalNodes += nodeCategories[i].length;
  }
  const cells = new Array(totalNodes + transport.length);
  let cellIndex = 0;

  const nodeMap = new Map();

  // Process nodes - use for loop for better performance
  for (let i = 0; i < nodeCategories.length; i++) {
    const category = nodeCategories[i];
    for (let j = 0; j < category.length; j++) {
      const node = category[j];
      const { cellType, name, module, parallelism, capacityPerHour, duration, details = {}, ...topLevel } = node;
      const nodeCellType = cellType || 'default';
      const code = nodeCellType.toUpperCase().split('_')[0];

      // Clone cached style to avoid mutations
      const baseAttrs = JSON.parse(JSON.stringify(getNodeStyleByType(nodeCellType)));

      if (code === 'TRI') {
        if (details.chevron1) baseAttrs.chevron1 = details.chevron1;
        if (details.chevron2) baseAttrs.chevron2 = details.chevron2;
      }

      // Determine shape type with simple if-else (faster than multiple conditions)
      let shapeType;
      if (code === 'TRI') {
        shapeType = 'appShapes.Tri';
      } else if (code === 'STOCK') {
        shapeType = 'appShapes.Stock';
      } else if (code === 'DIV') {
        shapeType = 'appShapes.Div';
      } else {
        shapeType = 'standard.Rectangle';
      }

      const rawName = String(name ?? '');
      const nameParts = rawName.split('_');
      const derivedCellName =
        code === 'TRI'
          ? rawName.replace(/^Sorter_/i, '')
          : nameParts.length > 1
            ? nameParts.slice(1).join('_')
            : rawName;

      // Build node object directly instead of using spread for better performance
      const fullNode = {
        id: details.id || node.id,
        type: shapeType,
        duration: duration || 1,
        name: nodeCellType,
        cellName: derivedCellName,
        position: details.position,
        size: details.size,
        angle: details.angle || 0,
        z: details.z || 0,
        ports: details.ports || {},
        sens: details.sens,
        maxDestinations: details.maxDestinations,
        inputBelt: details.inputBelt,
        controlType: details.controlType,
        exchangerType: details.exchangerType,
        loadingType: details.loadingType,
        checkInMode: details.checkInMode,
        linkType: details.linkType || 'Belt',
        module,
        parallelism: parallelism || 1,
        capacityPerHour,
        customAttributes: details.customAttributes || {},
        attrs: baseAttrs,
        outages: details.outages || [],
      };

      // Add sorter-specific props only for TRI
      if (code === 'TRI') {
        fullNode.loadFactor = node.loadFactor;
        fullNode.maxRecirculations = node.maxRecirculations;
        fullNode.orderedTransportDurations = node.orderedTransportDurations;
        fullNode.orderedTransportNodes = node.orderedTransportNodes;
        fullNode.recirculationRate = node.recirculationRate;
        fullNode.sorterType = node.sorterType;
        fullNode.theoreticalNumberOfPlaces = node.theoreticalNumberOfPlaces;
      }

      // Add divergence-specific props only for DIV
      if (code === 'DIV') {
        fullNode.moduleForBaggageRoutingRule = node.moduleForBaggageRoutingRule ?? '';
        fullNode.maxRecirculations = node.maxRecirculations ?? '';
      }

      // Copy remaining topLevel properties
      for (const key in topLevel) {
        if (!(key in fullNode)) {
          fullNode[key] = topLevel[key];
        }
      }

      nodeMap.set(fullNode.id, fullNode);
      cells[cellIndex++] = fullNode;
    }
  }

  // Process links - use for loop for better performance
  for (let i = 0; i < transport.length; i++) {
    const link = transport[i];
    const sourceId = link.details?.sourceId;
    const targetId = link.details?.targetId;

    if (!nodeMap.has(sourceId) || !nodeMap.has(targetId)) continue;

    const style = LINK_STYLES[link.transportType] || LINK_STYLES.default;

    cells[cellIndex++] = {
      type: 'standard.Link',
      attrs: {
        line: {
          stroke: style.stroke,
          strokeWidth: style.strokeWidth,
          strokeDasharray: style.strokeDasharray,
          sourceMarker: style.sourceMarker,
          targetMarker: style.targetMarker,
          opacity: 1,
        },
      },
      id: link.details.id,
      source: link.sourcePort ? { id: sourceId, port: link.sourcePort } : { id: sourceId },
      target: link.targetPort ? { id: targetId, port: link.targetPort } : { id: targetId },
      router: link.details.router || {
        name: 'normal',
        args: { padding: 10, useVertices: true, directions: ['top', 'right', 'bottom', 'left'] },
      },
      connector: link.details.connector || { name: 'normal' },
      linkType: link.details.linkType,
      module: link.details.module,
      length: link.details.length,
      duration: link.duration,
      parallelism: link.parallelism,
      capacityPerHour: link.capacityPerHour,
      speed: link.speed,
      distanceMeters: link.distanceMeters,
      useSpeed: link.useSpeed,
      interactive: {
        vertexAdd: true,
        vertexMove: true,
        vertexRemove: true,
        arrowheadMove: true,
        labelMove: true,
        useLinkTools: true,
        addVertex: true,
        removeVertex: true,
      },
      vertices: link.details.vertices || [],
      labels: [],
      outages: link.details.outages || [],
    };
  }

  // Trim the cells array to actual size
  cells.length = cellIndex;

  // Restore outages property on cells from tables.outages
  // This ensures that after import, each cell has its outages array populated
  // which is needed by the inspector UI (cell.get('outages'))
  if (Array.isArray(tables.outages) && tables.outages.length > 0) {
    // Build a map of cellId -> outages entries for efficient lookup
    const outagesByCellId = new Map();
    for (const outage of tables.outages) {
      if (outage.cellId) {
        if (!outagesByCellId.has(outage.cellId)) {
          outagesByCellId.set(outage.cellId, []);
        }
        // Store the outage without the cellId since it's stored on the cell itself
        const { cellId, ...outageData } = outage;
        outagesByCellId.get(outage.cellId).push(outageData);
      }
    }

    // Apply outages to cells
    for (let i = 0; i < cells.length; i++) {
      const cell = cells[i];
      if (cell && outagesByCellId.has(cell.id)) {
        cell.outages = outagesByCellId.get(cell.id);
      }
    }
  }

  // Compute modules
  const hasModulesKey = Object.prototype.hasOwnProperty.call(domainData, 'modules');
  let modules;

  if (hasModulesKey) {
    modules = Array.isArray(domainData.modules) ? domainData.modules.map(sanitizeModuleName) : [];
  } else {
    // Use Set for deduplication - more efficient than array operations
    const moduleSet = new Set();
    for (let i = 0; i < cells.length; i++) {
      const c = cells[i];
      if (!c) continue;
      const m = c.module;
      if (Array.isArray(m)) {
        for (let j = 0; j < m.length; j++) {
          const sanitized = sanitizeModuleName(m[j]);
          if (sanitized) moduleSet.add(sanitized);
        }
      } else if (typeof m === 'string' && m.trim()) {
        moduleSet.add(sanitizeModuleName(m));
      }
    }
    modules = Array.from(moduleSet);
  }

  return { cells, tables, modules };
};

export const convertGraphToJson = (graph) => {
  const { cells } = graph.toJSON();
  const nodeIdToNameMap = {};
  const resolvedNodeNames = {};
  const graphOutput = { graph: { transport: [] } };
  const rawTables = graph?.attributes?.tables || {};

  // Normalize stockChoiceTable: ensure we export with the correct field names
  // New format: currentModule, exitModule, earlyThreshold (integer seconds), stockName
  const normalizedTables = { ...rawTables };
  if (Array.isArray(normalizedTables.stockChoiceTable)) {
    normalizedTables.stockChoiceTable = normalizedTables.stockChoiceTable.map((row) => {
      return {
        currentModule: row.currentModule || row.entryModule || '',
        exitModule: row.exitModule || '',
        earlyThreshold: row.earlyThreshold ?? row.anticipationThreshold ?? 0,
        stockName: row.stockName || '',
      };
    });
  }
  graphOutput.tables = normalizedTables;

  const explicitModules = Array.isArray(graph?.attributes?.modules) ? graph.attributes.modules : [];
  const usedModules = getModulesFromGraph(graph);
  const merged = Array.from(new Set([...explicitModules, ...usedModules].map(sanitizeModuleName)));
  graphOutput.modules = merged;

  const prefixMap = {
    BE: 'checkIn',
    MEB: 'loadingStation',
    PIM: 'manualIdentificationPoint',
    CP: 'primaryControl',
    CC: 'complementaryControl',
    CU: 'lastControl',
    CONV: 'convergence',
    DIV: 'divergence',
    STOCK: 'stock',
    ECH: 'technologyExchanger',
    DEB: 'unloadingStation',
    PFT: 'finalSortingPoint',
    DM: 'lastMinuteSortingPoint',
    REBUT: 'rejectionPoint',
    TRI: 'sorter',
    DC: 'dropOff',
  };

  const specs = {
    loadingStation: ['cellType', 'name', 'binMaxInputSize', 'capacityPerHour', 'module', 'parallelism', 'duration'],
    checkIn: ['cellType', 'name', 'capacityPerHour', 'module', 'parallelism', 'unidentifiedRate', 'duration'],
    dropOff: ['cellType', 'name', 'capacityPerHour', 'module', 'parallelism', 'unidentifiedRate', 'duration'],
    primaryControl: ['cellType', 'name', 'capacityPerHour', 'module', 'parallelism', 'pendingRate', 'duration'],
    complementaryControl: ['cellType', 'name', 'capacityPerHour', 'module', 'parallelism', 'pendingRate', 'duration'],
    manualIdentificationPoint: ['cellType', 'name', 'capacityPerHour', 'module', 'parallelism', 'duration'],
    lastControl: ['cellType', 'name', 'capacityPerHour', 'module', 'parallelism', 'duration'],
    convergence: ['cellType', 'name', 'capacityPerHour', 'module', 'parallelism', 'duration'],
    divergence: [
      'cellType',
      'name',
      'capacityPerHour',
      'module',
      'parallelism',
      'moduleForBaggageRoutingRule',
      'maxRecirculations',
      'duration',
    ],
    finalSortingPoint: ['cellType', 'name', 'capacityPerHour', 'module', 'parallelism', 'duration'],
    lastMinuteSortingPoint: ['cellType', 'name', 'capacityPerHour', 'module', 'parallelism', 'duration'],
    rejectionPoint: ['cellType', 'name', 'capacityPerHour', 'module', 'parallelism', 'duration'],
    unloadingStation: ['cellType', 'name', 'capacityPerHour', 'module', 'parallelism', 'duration'],
    technologyExchanger: [
      'cellType',
      'name',
      'binMaxInputSize',
      'capacityPerHour',
      'module',
      'parallelism',
      'unloadingModule',
      'duration',
    ],
    stock: [
      'cellType',
      'name',
      'module',
      'parallelism',
      'capacityPerHour',
      'useSameExitingProperties',
      'exitingParallelism',
      'exitingCapacityPerHour',
      'theoreticalNumberOfPlaces',
      'initialBinStock',
      'maxBaggagesPerFlightPerMinute',
      'duration',
      'acceptsEmptyBins',
      'alpha',
    ],
    sorter: [
      'cellType',
      'name',
      'type',
      'loadFactor',
      'maxRecirculations',
      'module',
      'orderedTransportDurations',
      'orderedTransportNodes',
      'parallelism',
      'recirculationRate',
      'sorterType',
      'theoreticalNumberOfPlaces',
    ],
  };

  const linkCells = cells.filter((c) => c.type === 'standard.Link');
  cells.forEach((cell) => {
    if (cell.type !== 'standard.Link') {
      nodeIdToNameMap[cell.id] = cell.cellName;
    }
  });

  const resolveLabelById = (id) => resolvedNodeNames[id] || '';
  const resolveNodeNameById = (id) => nodeIdToNameMap[id] || '';

  const transportNameMap = {};
  linkCells.forEach((link) => {
    const sid = link.source?.id;
    const tid = link.target?.id;
    const name = `T_${resolveNodeNameById(sid)}_${resolveNodeNameById(tid)}`;
    transportNameMap[`${sid}|${tid}`] = name;
    transportNameMap[`${tid}|${sid}`] = name;
  });

  const sortPorts = (items = [], category = '') => {
    const arr = items.slice();
    if (category === 'sorter') {
      return arr.sort((a, b) => {
        const aKey = a?.attrs?.portLabel?.text ?? a?.id ?? '';
        const bKey = b?.attrs?.portLabel?.text ?? b?.id ?? '';
        return String(aKey).localeCompare(String(bKey), undefined, { numeric: true, sensitivity: 'base' });
      });
    }
    return arr.sort((a, b) => {
      const na = parseInt(String(a?.id ?? '').replace(/\D/g, ''), 10);
      const nb = parseInt(String(b?.id ?? '').replace(/\D/g, ''), 10);
      return (isNaN(na) ? Infinity : na) - (isNaN(nb) ? Infinity : nb);
    });
  };

  // sens=false (CW) -> keep CW order; sens=true (CCW) -> reverse order
  const portsInDisplayOrder = (sortedPorts = [], sens) => {
    if (!Array.isArray(sortedPorts)) return [];
    return sens ? [...sortedPorts].reverse() : sortedPorts;
  };

  // Durations aligned to the display order.
  // CCW rule: for each reversed port, take the CW previous neighbor's time (wrap).
  const durationsInDisplayOrder = (cwPorts = [], sens) => {
    const n = cwPorts.length || 0;
    if (n === 0) return [];
    if (!sens) {
      return cwPorts.map((p) => p?.time ?? '');
    }
    const reversed = [...cwPorts].reverse();
    return reversed.map((port) => {
      const idxInCW = cwPorts.findIndex((p) => p?.id === port?.id);
      if (idxInCW < 0) return '';
      const prevIdx = (idxInCW - 1 + n) % n;
      return cwPorts[prevIdx]?.time ?? '';
    });
  };

  cells.forEach((cell) => {
    if (cell.type === 'standard.Link') return;

    const name = cell.name;
    const prefix = name.split('_')[0].toUpperCase();
    const category = prefixMap[prefix];
    if (!category) return;

    const entry = {};
    specs[category].forEach((field) => {
      let value = '';
      switch (field) {
        case 'type': {
          value = prefix === 'TRI' ? 'Sorter' : (cell.type ?? '');
          break;
        }
        case 'name': {
          const fullName = cell.customAttributes?.fullName || '';
          const triLike = /^Tri[^_]*_/i;
          const normalized = triLike.test(fullName) ? fullName.replace(triLike, 'Sorter_') : fullName;
          value = (normalized || '') + (cell.cellName || '');
          break;
        }
        case 'maxBaggagesPerFlightPerMinute': {
          const v = cell[field];
          value = v === undefined || v === null || v === '' || Number.isNaN(v) ? 10000 : v;
          break;
        }
        case 'duration': {
          value = cell[field] ?? 1;
          break;
        }
        case 'parallelism': {
          value = cell[field] ?? 1;
          break;
        }
        case 'orderedTransportDurations': {
          const cwPorts = sortPorts(cell.ports?.items, category);
          value = durationsInDisplayOrder(cwPorts, !!cell.sens);
          break;
        }
        case 'orderedTransportNodes': {
          const thisId = cell.id;
          const thisName = resolveNodeNameById(thisId);

          // Map each of this node's ports to the connected link
          const linksByThisPort = new Map();
          const connected = linkCells.filter((l) => l.source?.id === thisId || l.target?.id === thisId);
          connected.forEach((l) => {
            const portId = l.source?.id === thisId ? l.source?.port : l.target?.port;
            if (portId) linksByThisPort.set(portId, l);
          });

          const cwPorts = sortPorts(cell.ports?.items, category);
          const displayPorts = portsInDisplayOrder(cwPorts, !!cell.sens);

          value = displayPorts.map((p) => {
            const link = linksByThisPort.get(p.id);
            if (!link) return '';

            // Prefer explicit destinations encoded on the port if present
            if (p.destination) {
              const destName = resolveNodeNameById(p.destination);
              return `T_${thisName}_${destName}`;
            }
            if (p.source) {
              const srcName = resolveNodeNameById(p.source);
              return `T_${srcName}_${thisName}`;
            }

            // Otherwise infer from the link direction
            const otherId = link.source?.id === thisId ? link.target?.id : link.source?.id;
            const otherName = resolveNodeNameById(otherId);
            const outgoing = link.source?.id === thisId;
            return outgoing ? `T_${thisName}_${otherName}` : `T_${otherName}_${thisName}`;
          });

          break;
        }
        default:
          value = cell[field] ?? '';
      }
      entry[field] = value;
    });

    entry.cellType = (cell.name || '').toString();

    if (
      category === 'stock' &&
      (entry.useSameExitingProperties === true || entry.useSameExitingProperties === 'true')
    ) {
      entry.exitingParallelism = entry.parallelism;
      entry.exitingCapacityPerHour = entry.capacityPerHour;
    }

    entry.details = {
      id: cell.id,
      z: cell.z || 0,
      angle: cell.angle || 0,
      position: cell.position || {},
      ports: cell.ports || {},
      size: cell.size || {},
      sens: cell.sens,
      maxDestinations: cell.maxDestinations,
      inputBelt: cell.inputBelt,
      controlType: cell.controlType,
      exchangerType: cell.exchangerType,
      loadingType: cell.loadingType,
      checkInMode: cell.checkInMode,
      customAttributes: cell.customAttributes || {},
      ...(prefix === 'TRI' && cell.attrs?.chevron1 && cell.attrs?.chevron2
        ? {
            chevron1: cell.attrs.chevron1,
            chevron2: cell.attrs.chevron2,
          }
        : {}),
      ...(cell.outages ? { outages: cell.outages } : {}),
    };

    resolvedNodeNames[cell.id] = entry.name;
    graphOutput.graph[category] = graphOutput.graph[category] || [];
    graphOutput.graph[category].push(entry);
  });

  linkCells.forEach((link) => {
    const transportEntry = {
      name: `T_${resolveNodeNameById(link.source?.id)}_${resolveNodeNameById(link.target?.id)}`,
      sourceName: resolveLabelById(link.source?.id),
      destinationName: resolveLabelById(link.target?.id),
      transportType: link.linkType === 'bac_vide' ? 'EmptyBins' : link.linkType === 'bagage_bac' ? 'Bins' : 'Belt',
      duration: link.duration ?? 1,
      parallelism: link.parallelism ?? 1,
      capacityPerHour: link.capacityPerHour,
      sourcePort: link.source?.port,
      targetPort: link.target?.port,
      speed: link.speed ?? null,
      distanceMeters: link.distanceMeters ?? null,
      useSpeed: link.useSpeed ?? false,
      details: {
        id: link.id,
        z: link.z || 0,
        linkType: link.linkType || '',
        module: link.module || '',
        length: link.length || '',
        vertices: link.vertices || [],
        router: link.router || {},
        connector: link.connector || {},
        sourceId: link.source?.id,
        targetId: link.target?.id,
        ...(link.outages ? { outages: link.outages } : {}),
      },
    };
    graphOutput.graph.transport.push(transportEntry);
  });

  return graphOutput;
};

export const exportGraphJsonFile = (graph) => {
  const graphOutput = convertGraphToJson(graph);
  const blob = new Blob([JSON.stringify(graphOutput, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'export.json';
  a.click();
  URL.revokeObjectURL(url);
};

export const updateCellOpacity = (graph, highlightedModules) => {
  if (!graph) return;

  graph.getElements().forEach((element) => {
    const elementModules = element.get('module') ?? [];
    const isVisible = highlightedModules.some((module) =>
      Array.isArray(elementModules) ? elementModules.includes(module) : sanitizeModuleName(elementModules) === module
    );
    const opacity = isVisible ? 1 : 0.3;
    element.attr('body/opacity', opacity);
    if (element.attr('chevron1')) {
      element.attr('label/opacity', opacity);
      element.attr('chevron1/opacity', opacity);
      element.attr('chevron2/opacity', opacity);
      element.prop('ports/groups/top/attrs/portBody/opacity', opacity);
      element.prop('ports/groups/bottom/attrs/portBody/opacity', opacity);
      element.prop('ports/groups/left/attrs/portBody/opacity', opacity);
      element.prop('ports/groups/right/attrs/portBody/opacity', opacity);
    }
  });

  graph.getLinks().forEach((link) => {
    const source = link.getSourceElement();
    const sourceVisible = source && highlightedModules.includes(sanitizeModuleName(source.get('module')));
    link.attr('line/opacity', sourceVisible ? 1 : 0.3);
  });
};

export const deleteModuleElements = (graph, deletedModule) => {
  const nodesToRemove = graph.getCells().filter((cell) => cell.get('module') === deletedModule);
  graph.removeCells(nodesToRemove);

  const linksToRemove = graph.getLinks().filter((link) => !link.getSourceElement() || !link.getTargetElement());
  graph.removeCells(linksToRemove);
};
